import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Plus, Trash2, Edit2, Users, Package, X, ChevronRight, Shield } from 'lucide-react'
import { PageHeader } from '@/components/common/PageHeader'
import { Card } from '@/components/common/Card'
import { Button } from '@/components/common/Button'
import { LoadingSpinner } from '@/components/common/LoadingSpinner'
import { api } from '@/services/api'
import {
  useTeams,
  useCreateTeam,
  useUpdateTeam,
  useDeleteTeam,
  useAddTeamMember,
  useRemoveTeamMember,
  useSetTeamProducts,
  useAccessLogs,
} from '@/hooks/useTeams'
import { UserRolesPage } from '@/pages/UserRolesPage'
import styles from './TeamManagementPage.module.css'

type Tab = 'teams' | 'audit' | 'roles'

const TEAM_ROLES = ['admin', 'manager', 'standard', 'readonly'] as const

function useAvailableProducts() {
  return useQuery({
    queryKey: ['products-list'],
    queryFn: async () => {
      const res = await api.get<{ id: number; name: string; slug: string }[]>('/products/')
      return res.data
    },
  })
}

function formatAction(action: string) {
  return action.replace(/_/g, ' ')
}

function timeAgo(iso: string) {
  const diff = Date.now() - new Date(iso).getTime()
  const min = Math.floor(diff / 60000)
  if (min < 60) return `${min}m ago`
  const hr = Math.floor(min / 60)
  if (hr < 24) return `${hr}h ago`
  return `${Math.floor(hr / 24)}d ago`
}

export function TeamManagementPage() {
  const [tab, setTab] = useState<Tab>('teams')
  const [selectedTeamId, setSelectedTeamId] = useState<number | null>(null)
  const [showNewTeam, setShowNewTeam] = useState(false)
  const [newTeamName, setNewTeamName] = useState('')
  const [newTeamDesc, setNewTeamDesc] = useState('')
  const [addEmail, setAddEmail] = useState('')
  const [addRole, setAddRole] = useState<string>('standard')
  const [editingTeamId, setEditingTeamId] = useState<number | null>(null)
  const [editName, setEditName] = useState('')
  const [editDesc, setEditDesc] = useState('')

  const { data: teams = [], isLoading } = useTeams()
  const { data: products = [] } = useAvailableProducts()
  const { data: logs = [], isLoading: logsLoading } = useAccessLogs()

  const createTeam = useCreateTeam()
  const updateTeam = useUpdateTeam()
  const deleteTeam = useDeleteTeam()
  const addMember = useAddTeamMember()
  const removeMember = useRemoveTeamMember()
  const setTeamProducts = useSetTeamProducts()

  const selectedTeam = teams.find((t) => t.id === selectedTeamId) ?? null

  const handleCreateTeam = async () => {
    if (!newTeamName.trim()) return
    await createTeam.mutateAsync({ name: newTeamName.trim(), description: newTeamDesc })
    setNewTeamName('')
    setNewTeamDesc('')
    setShowNewTeam(false)
  }

  const handleSaveEdit = async () => {
    if (!editingTeamId || !editName.trim()) return
    await updateTeam.mutateAsync({ id: editingTeamId, name: editName.trim(), description: editDesc })
    setEditingTeamId(null)
  }

  const handleDeleteTeam = async (id: number) => {
    if (!confirm('Delete this team? Members will lose product-scoped access.')) return
    await deleteTeam.mutateAsync(id)
    if (selectedTeamId === id) setSelectedTeamId(null)
  }

  const handleAddMember = async () => {
    if (!selectedTeam || !addEmail.trim()) return
    await addMember.mutateAsync({ teamId: selectedTeam.id, user_email: addEmail.trim(), role: addRole })
    setAddEmail('')
  }

  const handleToggleProduct = async (slug: string) => {
    if (!selectedTeam) return
    const current = selectedTeam.products.map((p) => p.product_slug)
    const next = current.includes(slug) ? current.filter((s) => s !== slug) : [...current, slug]
    await setTeamProducts.mutateAsync({ teamId: selectedTeam.id, product_slugs: next })
  }

  return (
    <div className={styles.page}>
      <PageHeader
        title="Teams & Access"
        subtitle="Manage teams, product-scoped roles, and audit logs"
      />

      <div className={styles.tabs}>
        <button
          className={`${styles.tab} ${tab === 'teams' ? styles.tabActive : ''}`}
          onClick={() => setTab('teams')}
        >
          <Users size={14} /> Teams
        </button>
        <button
          className={`${styles.tab} ${tab === 'roles' ? styles.tabActive : ''}`}
          onClick={() => setTab('roles')}
        >
          <Shield size={14} /> User Roles
        </button>
        <button
          className={`${styles.tab} ${tab === 'audit' ? styles.tabActive : ''}`}
          onClick={() => setTab('audit')}
        >
          <Shield size={14} /> Audit Log
        </button>
      </div>

      {tab === 'teams' && (
        <div className={styles.teamsLayout}>
          {/* Left: teams list */}
          <div className={styles.teamsList}>
            <div className={styles.listHeader}>
              <span className={styles.listTitle}>Teams ({teams.length})</span>
              <Button size="sm" onClick={() => setShowNewTeam(true)}>
                <Plus size={14} /> New Team
              </Button>
            </div>

            {showNewTeam && (
              <Card className={styles.newTeamCard}>
                <div className={styles.formRow}>
                  <input
                    className={styles.input}
                    placeholder="Team name *"
                    value={newTeamName}
                    onChange={(e) => setNewTeamName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleCreateTeam()}
                  />
                  <input
                    className={styles.input}
                    placeholder="Description (optional)"
                    value={newTeamDesc}
                    onChange={(e) => setNewTeamDesc(e.target.value)}
                  />
                </div>
                <div className={styles.formActions}>
                  <Button size="sm" onClick={handleCreateTeam} disabled={createTeam.isPending}>
                    {createTeam.isPending ? 'Creating…' : 'Create'}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setShowNewTeam(false)}>
                    Cancel
                  </Button>
                </div>
              </Card>
            )}

            {isLoading ? (
              <LoadingSpinner />
            ) : teams.length === 0 ? (
              <p className={styles.empty}>No teams yet. Create one to get started.</p>
            ) : (
              teams.map((team) => (
                <div
                  key={team.id}
                  role="button"
                  tabIndex={0}
                  className={`${styles.teamCard} ${selectedTeamId === team.id ? styles.teamCardActive : ''}`}
                  onClick={() => setSelectedTeamId(selectedTeamId === team.id ? null : team.id)}
                  onKeyDown={(e) => e.key === 'Enter' && setSelectedTeamId(selectedTeamId === team.id ? null : team.id)}
                >
                  {editingTeamId === team.id ? (
                    <div onClick={(e) => e.stopPropagation()} className={styles.editForm}>
                      <input
                        className={styles.input}
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        placeholder="Team name"
                      />
                      <input
                        className={styles.input}
                        value={editDesc}
                        onChange={(e) => setEditDesc(e.target.value)}
                        placeholder="Description"
                      />
                      <div className={styles.formActions}>
                        <Button size="sm" onClick={handleSaveEdit} disabled={updateTeam.isPending}>
                          Save
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setEditingTeamId(null)}>
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className={styles.teamCardTop}>
                        <div className={styles.teamName}>
                          <ChevronRight
                            size={14}
                            className={`${styles.chevron} ${selectedTeamId === team.id ? styles.chevronOpen : ''}`}
                          />
                          {team.name}
                        </div>
                        <div className={styles.teamCardActions} onClick={(e) => e.stopPropagation()}>
                          <button
                            className={styles.iconBtn}
                            title="Edit"
                            onClick={() => {
                              setEditingTeamId(team.id)
                              setEditName(team.name)
                              setEditDesc(team.description)
                            }}
                          >
                            <Edit2 size={13} />
                          </button>
                          <button
                            className={`${styles.iconBtn} ${styles.iconBtnDanger}`}
                            title="Delete"
                            onClick={() => handleDeleteTeam(team.id)}
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </div>
                      {team.description && <p className={styles.teamDesc}>{team.description}</p>}
                      <div className={styles.teamMeta}>
                        <span><Users size={12} /> {team.members.length} member{team.members.length !== 1 ? 's' : ''}</span>
                        <span><Package size={12} /> {team.products.length} product{team.products.length !== 1 ? 's' : ''}</span>
                      </div>
                      {team.products.length > 0 && (
                        <div className={styles.productBadges}>
                          {team.products.map((p) => (
                            <span key={p.product_slug} className={styles.badge}>{p.product_slug}</span>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))
            )}
          </div>

          {/* Right: team detail */}
          {selectedTeam && (
            <div className={styles.teamDetail}>
              <div className={styles.detailHeader}>
                <h3 className={styles.detailTitle}>{selectedTeam.name}</h3>
                <button className={styles.iconBtn} onClick={() => setSelectedTeamId(null)}>
                  <X size={16} />
                </button>
              </div>

              {/* Product assignments */}
              <Card className={styles.detailSection}>
                <h4 className={styles.sectionTitle}><Package size={14} /> Product Access</h4>
                {products.length === 0 ? (
                  <p className={styles.empty}>No products available.</p>
                ) : (
                  <div className={styles.productGrid}>
                    {products.map((p) => {
                      const assigned = selectedTeam.products.some((tp) => tp.product_slug === p.slug)
                      return (
                        <label key={p.slug} className={styles.productCheck}>
                          <input
                            type="checkbox"
                            checked={assigned}
                            onChange={() => handleToggleProduct(p.slug)}
                          />
                          <span>{p.name}</span>
                          <span className={styles.slugHint}>({p.slug})</span>
                        </label>
                      )
                    })}
                  </div>
                )}
              </Card>

              {/* Members */}
              <Card className={styles.detailSection}>
                <h4 className={styles.sectionTitle}><Users size={14} /> Members</h4>

                <div className={styles.addMemberRow}>
                  <input
                    className={styles.input}
                    placeholder="Email address"
                    value={addEmail}
                    onChange={(e) => setAddEmail(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleAddMember()}
                  />
                  <select
                    className={styles.select}
                    value={addRole}
                    onChange={(e) => setAddRole(e.target.value)}
                  >
                    {TEAM_ROLES.map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                  <Button size="sm" onClick={handleAddMember} disabled={addMember.isPending}>
                    Add
                  </Button>
                </div>

                {selectedTeam.members.length === 0 ? (
                  <p className={styles.empty}>No members. Add one above.</p>
                ) : (
                  <table className={styles.memberTable}>
                    <thead>
                      <tr>
                        <th>Email</th>
                        <th>Role</th>
                        <th>Added by</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedTeam.members.map((m) => (
                        <tr key={m.id}>
                          <td>{m.user_email}</td>
                          <td><span className={styles.roleBadge}>{m.role}</span></td>
                          <td className={styles.muted}>{m.added_by || '—'}</td>
                          <td>
                            <button
                              className={`${styles.iconBtn} ${styles.iconBtnDanger}`}
                              onClick={() => removeMember.mutate({ teamId: selectedTeam.id, memberId: m.id })}
                              title="Remove"
                            >
                              <X size={13} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </Card>
            </div>
          )}
        </div>
      )}

      {tab === 'roles' && <UserRolesPage />}

      {tab === 'audit' && (
        <Card>
          <h3 className={styles.sectionTitle}><Shield size={14} /> RBAC Audit Log</h3>
          {logsLoading ? (
            <LoadingSpinner />
          ) : logs.length === 0 ? (
            <p className={styles.empty}>No audit log entries yet.</p>
          ) : (
            <table className={styles.auditTable}>
              <thead>
                <tr>
                  <th>User</th>
                  <th>Action</th>
                  <th>Resource</th>
                  <th>ID</th>
                  <th>When</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <tr key={log.id}>
                    <td>{log.user_email}</td>
                    <td><span className={styles.actionBadge}>{formatAction(log.action)}</span></td>
                    <td>{log.resource_type}</td>
                    <td className={styles.muted}>{log.resource_id || '—'}</td>
                    <td className={styles.muted} title={log.timestamp}>{timeAgo(log.timestamp)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      )}
    </div>
  )
}
